/**
 * Footer for the New Message modal with step-aware buttons.
 * Step 0: Cancel + Next. Step 1: Back + Send.
 */

import { ArrowRightIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { IntegrationType } from '@/shared/types/integration';

interface NewMessageFooterProps {
  isSending: boolean;
  step: 0 | 1;
  selectedIntegrationType?: IntegrationType;
  isNextLoading: boolean;
  isNewContact: boolean;
  onNext: () => void;
  onBack: () => void;
  onSend: () => void;
  onCloseAttempt: () => void;
}

export const NewMessageFooter = ({
  isSending,
  step,
  selectedIntegrationType,
  isNextLoading,
  isNewContact,
  onNext,
  onBack,
  onSend,
  onCloseAttempt,
}: NewMessageFooterProps) => {
  const { t } = useTranslation();

  if (step === 0) {
    return (
      <DialogFooter>
        <Button variant="ghost-neutral" onClick={onCloseAttempt}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={onNext} isLoading={isNextLoading}>
          {t('common.next')}
          <ArrowRightIcon />
        </Button>
      </DialogFooter>
    );
  }

  let sendLabel = t('common.send');
  if (isNewContact) {
    sendLabel = t('conversation.new_message.contact_lookup.confirm_send');
  }
  if (isSending) {
    sendLabel = t('common.sending');
  }

  return (
    <div className="flex flex-col gap-4">
      {isNewContact && (
        <Alert variant="default">
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>{t('conversation.new_message.contact_lookup.confirm_title')}</AlertTitle>
          <AlertDescription>
            {t('conversation.new_message.contact_lookup.confirm_description')}
          </AlertDescription>
        </Alert>
      )}
      <DialogFooter className="flex items-center gap-4">
        {selectedIntegrationType === IntegrationType.WHATSAPP && (
          <div className="flex-1">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <InfoIcon className="h-3 w-3 shrink-0" />
              {t('chatBox.incur_charges')}
            </p>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="ghost-neutral" onClick={onBack}>
            {t('common.previous')}
          </Button>
          <Button variant="primary" onClick={onSend} isLoading={isSending}>
            {sendLabel}
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
};
